import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import folderPermissionService from '../../services/FolderPermissionService';

const UserAccessExplanation = ({ open, onClose, folderPath, username }) => {
    const [loading, setLoading] = useState(true);
    const [accessData, setAccessData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;

        const fetchAccessExplanation = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await folderPermissionService.explainUserAccess(folderPath, username);
                setAccessData(response);
            } catch (err) {
                console.error('Error fetching access explanation:', err);
                setError('Failed to retrieve access information. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchAccessExplanation();
    }, [folderPath, username, open]);

    const renderAccessTypeChip = (accessType) => {
        if (!accessType) return 'No Access';

        let color;
        switch (accessType.toLowerCase()) {
            case 'read':
            case 'readonly':
                color = 'info.main';
                break;
            case 'write':
            case 'readwrite':
                color = 'success.main';
                break;
            case 'admin':
                color = 'warning.main';
                break;
            default:
                color = 'text.secondary';
        }

        return (
            <Typography component="span" sx={{ fontWeight: 'bold', color }}>
                {accessType}
            </Typography>
        );
    };

    const renderAccessPath = () => {
        if (!accessData || !accessData.accessPath) return null;

        return (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Access Path
                </Typography>

                <List dense>
                    {accessData.accessPath.map((step, index) => (
                        <React.Fragment key={index}>
                            <ListItem>
                                <ListItemIcon>
                                    {step.type === 'folder' && <FolderIcon color="primary" />}
                                    {step.type === 'user' && <PersonIcon color="primary" />}
                                    {step.type === 'group' && <GroupIcon color="secondary" />}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography>
                                            {step.name}
                                            {step.access && <> - Access: {renderAccessTypeChip(step.access)}</>}
                                        </Typography>
                                    }
                                    secondary={step.description}
                                />
                            </ListItem>
                            {index < accessData.accessPath.length - 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                                    <ArrowRightAltIcon />
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Access Explanation: {username}
                <Typography variant="subtitle2" color="text.secondary">
                    Folder: {folderPath}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : accessData ? (
                    <Box>
                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ mr: 2 }}>
                                {accessData.hasAccess ? (
                                    <CheckCircleIcon color="success" fontSize="large" />
                                ) : (
                                    <BlockIcon color="error" fontSize="large" />
                                )}
                            </Box>
                            <Typography variant="h6">
                                {accessData.hasAccess ? (
                                    <>User has {renderAccessTypeChip(accessData.accessType)} access to this folder</>
                                ) : (
                                    <>User does not have access to this folder</>
                                )}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {renderAccessPath()}

                        {accessData.directPermissions && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Direct Permissions
                                </Typography>
                                <Typography>
                                    {accessData.directPermissions
                                        ? renderAccessTypeChip(accessData.directPermissions)
                                        : 'None'}
                                </Typography>
                            </Paper>
                        )}

                        {accessData.groupPermissions && accessData.groupPermissions.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Group Permissions
                                </Typography>
                                <List dense>
                                    {accessData.groupPermissions.map((group, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <GroupIcon color="secondary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography>
                                                        {group.name} - Access: {renderAccessTypeChip(group.access)}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        {accessData.inheritedPermissions && accessData.inheritedPermissions.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Inherited Permissions
                                </Typography>
                                <List dense>
                                    {accessData.inheritedPermissions.map((perm, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <FolderIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography>
                                                        From {perm.folderPath} - Access:{' '}
                                                        {renderAccessTypeChip(perm.access)}
                                                    </Typography>
                                                }
                                                secondary={perm.description}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        {accessData.parentPath && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Parent Folder Permissions
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <ListItemIcon>
                                        <FolderIcon color="primary" />
                                    </ListItemIcon>
                                    <Typography>
                                        Parent folder: {accessData.parentPath} - Permission:{' '}
                                        {renderAccessTypeChip(accessData.parentPermission)}
                                    </Typography>
                                </Box>
                                {accessData.inheritsFromParent && (
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        This folder inherits permissions from its parent folder.
                                    </Alert>
                                )}
                            </Paper>
                        )}

                        {accessData.effectivePermission && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Effective Permission
                                </Typography>
                                <Typography>
                                    Final calculated permission: {renderAccessTypeChip(accessData.effectivePermission)}
                                    {accessData.isAdmin && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            User has administrative privileges.
                                        </Alert>
                                    )}
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                ) : (
                    <Alert severity="info">No access information available</Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserAccessExplanation;
